/* eslint-disable no-console */
/* eslint-disable consistent-return */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface VersionInfo {
    version: string;
    buildNumber?: string;
    deployedAt?: string;
}

/**
 * Service to check for app version updates.
 * It fetches a version file generated at build time and compares it with the current version.
 * It supports periodic checking and notifying listeners when a new version is available.
 */
export class VersionService {
    private static instance: VersionService;

    private currentVersion: string;

    private checkInterval: number = 5 * 60 * 1000; // 5 minutes

    private intervalId: NodeJS.Timeout | null = null;

    private listeners: Array<(hasUpdate: boolean, newVersion?: string) => void> = [];

    private constructor() {
        // Assume APP_VERSION is injected at build time - Check webpack.common.js for details
        this.currentVersion = APP_VERSION;
    }

    static getInstance(): VersionService {
        if (!VersionService.instance) {
            VersionService.instance = new VersionService();
        }
        return VersionService.instance;
    }

    /**
     * Start periodic version checking
     * @param intervalMs Check interval in milliseconds
     */
    startChecking(intervalMs: number = this.checkInterval): void {
        this.checkInterval = intervalMs;

        // Check immediately on start
        this.checkForUpdates();

        // Then check periodically
        this.intervalId = setInterval(() => {
            this.checkForUpdates();
        }, this.checkInterval);
    }

    /**
     * Stop periodic version checking
     */
    stopChecking(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Manually check for version updates
     */
    async checkForUpdates(): Promise<boolean> {
        try {
            const response = await fetch('/version.json', {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
            });

            if (!response.ok) {
                console.warn('Failed to fetch version info:', response.status);
                return false;
            }

            const versionInfo: VersionInfo = await response.json();
            const hasUpdate = versionInfo.version !== this.currentVersion;

            if (hasUpdate) {
                console.log(`New version available: ${versionInfo.version} (current: ${this.currentVersion})`);
                this.notifyListeners(true, versionInfo.version);
            }

            return hasUpdate;
        } catch (error) {
            console.warn('Error checking for version updates:', error);
            return false;
        }
    }

    /**
     * Subscribe to version update notifications
     */
    onVersionUpdate(callback: (hasUpdate: boolean, newVersion?: string) => void): () => void {
        this.listeners.push(callback);

        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private notifyListeners(hasUpdate: boolean, newVersion?: string): void {
        this.listeners.forEach((callback) => {
            try {
                callback(hasUpdate, newVersion);
            } catch (error) {
                console.error('Error in version update listener:', error);
            }
        });
    }

    /**
     * Get current app version
     */
    getCurrentVersion(): string {
        return this.currentVersion;
    }
}

/**
 * React hook for version checking
 */
export function useVersionChecker(
    options: {
        enabled?: boolean;
        checkInterval?: number;
        onUpdateAvailable?: (newVersion: string) => void;
    } = {},
): {
    hasUpdate: boolean;
    newVersion: string | null;
    checkNow: () => Promise<boolean>;
    currentVersion: string;
} {
    const { enabled = true, checkInterval = 5 * 60 * 1000, onUpdateAvailable } = options;
    const [hasUpdate, setHasUpdate] = useState(false);
    const [newVersion, setNewVersion] = useState<string | null>(null);
    const versionService = useRef(VersionService.getInstance()).current;

    useEffect(() => {
        if (!enabled) return;

        const unsubscribe = versionService.onVersionUpdate((update, version) => {
            setHasUpdate(update);
            setNewVersion(version || null);

            if (update && version && onUpdateAvailable) {
                onUpdateAvailable(version);
            }
        });

        versionService.startChecking(checkInterval);

        return () => {
            unsubscribe();
            versionService.stopChecking();
        };
    }, [enabled, checkInterval, onUpdateAvailable, versionService]);

    const checkNow = useCallback(async () => versionService.checkForUpdates(), [versionService]);

    return {
        hasUpdate,
        newVersion,
        checkNow,
        currentVersion: versionService.getCurrentVersion(),
    };
}
